/**
 * "내 계정" 화면과 앱 사이의 다리 계약.
 *
 * 2026-08-26 사용자 지시로 계정 화면이 앱에서 웹으로 옮겨 갔다: **"프로필 페이지도 각
 * 앱에서 지원하기 페이지처럼 웹뷰로 구현해주고. (세션 등 완벽히 유지) (공통페이지 1개로
 * 모두 관리) (테마 유지)"** — 문의 화면이 먼저 간 길과 같다 (docs/support.md).
 *
 * ## 세션을 넘기지 않는다 — 앱이 대신 부른다
 *
 * 문의 화면은 로그인이 없어서 주소만 넘기면 끝났다. 계정 화면은 다르다: 그 사람의
 * Keycloak 세션이 있어야 프로필을 읽고 비밀번호를 바꾼다. 방법이 둘 있었다.
 *
 * | 방법 | 세션 | 대가 |
 * |---|---|---|
 * | 토큰을 페이지에 넘긴다 | 액세스 토큰이 웹뷰 JS 안에 산다 | realm 여섯·서비스 넷에 **CORS 를 열어야** 하고, 만료되면 갱신 토큰까지 넘기게 된다 |
 * | **다리를 놓는다 (이 파일)** | 토큰이 앱 밖으로 안 나간다 | 페이지가 부를 수 있는 것이 이 목록으로 고정된다 |
 *
 * **뒤를 골랐다.** 앱은 이미 `AccountClient` 로 이 호출들을 전부 하고 있고, 페이지는 그
 * 결과를 그리기만 하면 된다. 토큰이 페이지에 없으므로 "세션을 완벽히 유지한다"가 문장이
 * 아니라 구조가 된다 — 페이지가 유출할 세션 자체가 없다.
 *
 * 대가는 정직하게 적어 둔다: **여기 없는 동작은 앱을 다시 배포해야 는다.** 문구·배치·언어를
 * 고치는 데 앱 배포가 끼지 않는다는 것이 이전의 목적이었고 그건 지켰지만, "계정에서 할 수
 * 있는 일"을 늘리는 것은 여전히 앱 배포다.
 *
 * ## 오가는 모양
 *
 * ```
 * 페이지 → 앱   window.ReactNativeWebView.postMessage(JSON.stringify(AccountRequest))
 * 앱 → 페이지   webView.injectJavaScript(`window.${BRIDGE_GLOBAL}.resolve(<AccountReply>)`)
 * ```
 *
 * 요청마다 `id` 를 달아 짝을 맞춘다 — 사진 올리기처럼 오래 걸리는 것과 프로필 읽기가
 * 섞여 돌아오면 순서로는 못 가린다.
 */

/** 전 서비스가 여는 한 주소. 서비스는 `?service=` 로 갈린다 (문의 화면과 같은 규칙) */
export const ACCOUNT_PAGE_URL = 'https://cottonray.com/account'

/**
 * 페이지가 앱에 심어 두는 전역 이름. **앱이 이 이름으로 답을 꽂는다** — 양쪽이 같은
 * 문자열을 봐야 하므로 계약에 둔다.
 */
export const BRIDGE_GLOBAL = '__cottonrayAccount'

/** 앱이 할 수 있는 일. 화면은 이 목록만 부른다 */
export type AccountMethod =
  | 'profile'
  | 'capabilities'
  | 'linkedAccounts'
  | 'avatar'
  | 'updateProfile'
  | 'changePassword'
  | 'pickPhoto'
  | 'deletePhoto'
  | 'deleteAccount'
  | 'logout'
  | 'close'

export type SocialProviderId = 'kakao' | 'google' | 'apple'

export interface AccountProfile {
  /** 화면에 보여 줄 이름. 없으면 아이디를 쓴다 */
  displayName: string
  username: string
  email: string | null
  /** 카카오·구글·애플 중 무엇으로 들어왔나. 비밀번호 로그인이면 null */
  socialProvider: SocialProviderId | null
}

export interface AccountLinkedProvider {
  providerAlias: string
  displayName: string
  linkedUsername: string | null
  connected: boolean
}

/**
 * 이 앱·이 서버가 지금 실제로 할 수 있는 것. **화면이 이걸 보고 메뉴를 그린다** —
 * 눌렀더니 안 되는 항목을 보여 주지 않기 위한 것이다.
 */
export interface AccountCapabilities {
  canChangePassword: boolean
  canDeleteAccount: boolean
  canUploadPhoto: boolean
  /**
   * 사진을 앱의 카메라·앨범으로 고를 수 있나. 웹의 `<input type="file">` 을 쓰지 않는
   * 이유는 그쪽이 앱에 사진 권한을 되살리기 때문이다 (문의 화면에서 걷어낸 그 권한이다).
   */
  canPickPhoto: boolean
  /**
   * 계정을 지우면 **이 서비스에서** 무엇이 사라지는지. 확인 문구에 그대로 들어간다 —
   * "되돌릴 수 없다"만 적으면 무엇을 잃는지 모른 채 누른다. 앱마다 다르므로 앱이 준다.
   */
  deletionNote: string
}

/** 페이지 → 앱 */
export type AccountRequest =
  | { id: string; method: 'profile' }
  | { id: string; method: 'capabilities' }
  | { id: string; method: 'linkedAccounts' }
  | { id: string; method: 'avatar' }
  | { id: string; method: 'updateProfile'; displayName: string; email: string }
  | { id: string; method: 'changePassword'; currentPassword: string; newPassword: string }
  | { id: string; method: 'pickPhoto'; source: 'camera' | 'library' }
  | { id: string; method: 'deletePhoto' }
  | { id: string; method: 'deleteAccount' }
  | { id: string; method: 'logout' }
  | { id: string; method: 'close' }

/**
 * `id` 를 빼고 부르는 쪽이 채우는 모양.
 *
 * **`Omit<AccountRequest, 'id'>` 를 쓰면 안 된다** — `Omit` 은 유니온 위에서 분배되지 않아
 * 열한 갈래의 **공통 필드만** 남는다(`method` 하나). 그러면 `updateProfile` 의
 * `displayName` 이 "없는 속성"이 되어 컴파일이 막힌다. 조건부 타입은 유니온 위에서
 * 분배되므로 갈래마다 따로 벗겨진다.
 */
export type AccountCall = AccountRequest extends infer T
  ? T extends { id: string }
    ? Omit<T, 'id'>
    : never
  : never

/**
 * 실패의 종류. **화면이 문구를 고르는 축이다** — 앱이 만든 한국어 문장을 그대로 띄우면
 * 페이지의 언어 설정을 무시하게 된다. 그래서 사유만 넘기고 말은 페이지가 한다.
 */
export type AccountFailure = 'network' | 'denied' | 'conflict' | 'invalid' | 'unsupported'

/** 앱 → 페이지 */
export type AccountReply =
  | { id: string; ok: true; value: AccountValue }
  | { id: string; ok: false; reason: AccountFailure }

export type AccountValue =
  | null
  | AccountProfile
  | AccountCapabilities
  | AccountLinkedProvider[]
  /** 사진은 data URL 로 온다 — `<img>` 에 인증 헤더를 실을 방법이 없다 */
  | string

/** 오래 걸리는 것(사진 고르기)까지 감안한 한 번의 대기 상한 */
export const BRIDGE_TIMEOUT_MS = 120_000
